import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface PrivacyPolicyProps {
  onBack: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  useSEOMetaTags({
    title: 'Privacy Policy - Orcaseotools | Sofia Host LLC',
    description: 'Privacy Policy for Orcaseotools. Learn how Sofia Host LLC collects, uses, and protects your personal information when using our professional SEO and AEO audit tools. GDPR and CCPA compliant privacy practices.',
    keywords: 'privacy policy, data protection, GDPR, CCPA, privacy rights, Orcaseotools privacy, Stripe, SEO tools privacy, website audit privacy',
    canonicalUrl: 'https://orcaseotools.com/privacy-policy',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Privacy Policy - Orcaseotools",
      "description": "Privacy Policy for Orcaseotools SEO and AEO audit tools. Learn how we protect your data.",
      "url": "https://orcaseotools.com/privacy-policy",
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Orcaseotools",
        "url": "https://orcaseotools.com"
      },
      "about": {
        "@type": "Thing",
        "name": "Privacy Policy"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Sofia Host LLC",
        "legalName": "Sofia Host LLC"
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-6">
          <header>
            <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-slate-400 text-sm">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </header>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">1. Introduction</h2>
            <p className="text-slate-300 leading-relaxed">
              At <strong>Orcaseotools</strong>, operated by <strong>Sofia Host LLC</strong> (a New Mexico limited liability company, Entity ID: 4424925), we are dedicated to safeguarding the privacy and security of our customers' personal information. This Privacy Policy explains how we collect, use, and protect your data when you interact with our website (orcaseotools.com) or use our <strong>SEO and AEO audit tools</strong>.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">2. Information We Collect</h2>
            <p className="text-slate-300 leading-relaxed">
              We collect various types of information to provide and improve our services, including:
            </p>
            <ul className="list-disc pl-5 text-slate-300 leading-relaxed space-y-2">
              <li><strong>Personal Data:</strong> We may collect your name, email address, WhatsApp number, and the website URLs you wish to analyze.</li>
              <li><strong>Usage Data:</strong> We automatically collect information about how you interact with our services, such as your IP address, browser type, and the pages you visit.</li>
              <li><strong>Cookies:</strong> We use cookies to store user preferences, session information, and track usage data to improve our platform.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">3. Stripe Payment Processing Disclosure</h2>
            <p className="text-slate-300 leading-relaxed font-semibold">
              We use Stripe for payment processing. Stripe may collect personal data including via cookies and similar technologies. The personal data Stripe collects may include your payment card details, billing address, and transaction history. This data is processed in accordance with Stripe's Privacy Policy (<a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">https://stripe.com/privacy</a>).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">4. How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed">
              We use the collected data for various purposes:
            </p>
            <ul className="list-disc pl-5 text-slate-300 leading-relaxed space-y-2">
              <li><strong>Service Delivery:</strong> To generate and deliver your SEO and AEO audits.</li>
              <li><strong>Communication:</strong> To send reports, notifications, and customer support messages via email or WhatsApp.</li>
              <li><strong>Analytics:</strong> To monitor and analyze usage patterns to improve our service offerings.</li>
              <li><strong>Improvement:</strong> To enhance user experience, security, and the accuracy of our audit tools.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">5. Data Sharing and Third Parties</h2>
            <p className="text-slate-300 leading-relaxed">
              We do not sell your personal data. We only share your data with trusted third parties necessary for delivering our services:
            </p>
            <ul className="list-disc pl-5 text-slate-300 leading-relaxed space-y-2">
              <li><strong>Payment Processors:</strong> We use Stripe to handle secure payments as detailed above.</li>
              <li><strong>Analytics Providers:</strong> We use third-party analytics services to understand website traffic and usage behavior.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">6. Data Retention</h2>
            <p className="text-slate-300 leading-relaxed">
              We retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">7. Your Rights Under GDPR</h2>
            <p className="text-slate-300 leading-relaxed">
              If you are a resident of the European Economic Area (EEA), you have certain data protection rights:
            </p>
            <ul className="list-disc pl-5 text-slate-300 leading-relaxed space-y-2">
              <li><strong>Right of Access:</strong> You can request a copy of the personal data we hold about you.</li>
              <li><strong>Right to Rectification:</strong> You can request that we correct any inaccurate or incomplete personal data.</li>
              <li><strong>Right to Erasure:</strong> You can request that we delete your personal data under certain conditions.</li>
              <li><strong>Right to Data Portability:</strong> You can request a transfer of your data to another organization or directly to you.</li>
              <li><strong>Right to Object and Restrict Processing:</strong> You have the right to object to or ask us to restrict the processing of your personal data.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">8. Your Rights Under CCPA</h2>
            <p className="text-slate-300 leading-relaxed">
              If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights regarding your personal information:
            </p>
            <ul className="list-disc pl-5 text-slate-300 leading-relaxed space-y-2">
              <li><strong>Right to Know:</strong> You have the right to request disclosures about our data collection and sales practices in connection with you.</li>
              <li><strong>Right to Delete:</strong> You can request the deletion of your personal information that we have collected.</li>
              <li><strong>Right to Opt-Out:</strong> We do not sell your personal data. However, you have the right to direct us not to sell your personal information.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">9. Children's Privacy</h2>
            <p className="text-slate-300 leading-relaxed">
              Our services are not intended for use by children under the age of 13. We do not knowingly collect personal identifiable information from children under 13. If we discover that a child under 13 has provided us with personal data, we will immediately delete this information from our servers.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">10. International Data Transfers</h2>
            <p className="text-slate-300 leading-relaxed">
              Your information may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction. Your consent to this Privacy Policy represents your agreement to that transfer.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">11. Security Measures</h2>
            <p className="text-slate-300 leading-relaxed">
              The security of your data is important to us. We employ industry-standard security measures including SSL encryption and secure servers to safeguard your personal information. However, remember that no method of transmission over the Internet or method of electronic storage is 100% secure.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">12. Cookie Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our service and hold certain information. We use essential cookies for authentication and security, preference cookies to remember your settings, and analytics cookies to understand how our services are used.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">13. Changes to This Policy</h2>
            <p className="text-slate-300 leading-relaxed">
              We retain the right to amend this Privacy Policy at any time. Any revisions will be published on this page with an updated "Last updated" date. We recommend checking this page periodically to stay informed about how we manage your information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-indigo-400">14. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed">
              If you have any questions or comments about our Privacy Policy, please reach out to us at:
            </p>
            <div className="bg-slate-900/50 rounded-lg p-6 space-y-3">
              <p className="text-slate-300"><strong>Company:</strong> Sofia Host LLC</p>
              <p className="text-slate-300"><strong>Address:</strong> 1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</p>
              <p className="text-slate-300"><strong>Registered Agent:</strong> Registered Agents Inc</p>
              <p className="text-slate-300"><strong>Entity ID:</strong> 4424925</p>
              <p className="text-slate-300"><strong>Jurisdiction:</strong> New Mexico, USA</p>
              <p className="text-slate-300"><strong>Email:</strong> <a href="mailto:contact@orcaseotools.com" className="text-indigo-400 hover:underline">contact@orcaseotools.com</a></p>
              <p className="text-slate-300"><strong>Phone:</strong> <a href="tel:+16065577288" className="text-indigo-400 hover:underline">+1 (606) 557-7288</a></p>
              <p className="text-slate-300"><strong>Website:</strong> <a href="https://orcaseotools.com" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">orcaseotools.com</a></p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm text-center">
              Thank you for entrusting <strong>Orcaseotools</strong> with your personal information. We value your privacy and are committed to protecting your data.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};
